import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const AngleOfImpactCalculator = () => {
  const [inputData, setInputData] = useState({
    height: '',
    horizontalVelocity: '',
    result: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateAngleOfImpact = () => {
    const { height, horizontalVelocity } = inputData;
    
    // Validate input
    if (!height || !horizontalVelocity) {
      setError('Please enter both height and horizontal velocity');
      return;
    }

    // Validate numeric input
    if (isNaN(height) || isNaN(horizontalVelocity)) {
      setError('Please enter valid numbers for height and horizontal velocity');
      return;
    }

    // Convert to numbers
    const numericHeight = parseFloat(height);
    const numericHorizontalVelocity = parseFloat(horizontalVelocity);

    // Constants
    const gravity = 9.81; // m/s²

    // Calculate time of flight
    const timeOfFlight = Math.sqrt(2 * numericHeight / gravity);

    // Calculate vertical velocity at impact
    const verticalVelocity = gravity * timeOfFlight;

    // Calculate angle of impact in radians
    const angleRadians = Math.atan(verticalVelocity / numericHorizontalVelocity);

    // Convert to degrees
    const angleDegrees = angleRadians * (180 / Math.PI);

    // Create visualization data
    const values = [numericHorizontalVelocity, verticalVelocity];
    const labels = ['Horizontal Velocity', 'Vertical Velocity'];

    const chartData = {
      labels: labels,
      datasets: [{
        label: 'Velocity Components',
        data: values,
        backgroundColor: [
          'rgba(255, 99, 132, 0.5)',
          'rgba(54, 162, 235, 0.5)'
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)'
        ],
        borderWidth: 1
      }]
    };

    const calculationSteps = [
      'Step 1: Calculate time of flight',
      `Time = sqrt(2 * height / gravity) = sqrt(2 * ${numericHeight} / 9.81) ≈ ${timeOfFlight.toFixed(2)} seconds`,
      '\nStep 2: Calculate vertical velocity at impact',
      `Vertical Velocity = gravity * time = 9.81 * ${timeOfFlight.toFixed(2)} ≈ ${verticalVelocity.toFixed(2)} m/s`,
      '\nStep 3: Calculate angle of impact',
      `Angle = arctan(vertical velocity / horizontal velocity) = arctan(${verticalVelocity.toFixed(2)} / ${numericHorizontalVelocity}) ≈ ${angleDegrees.toFixed(2)} degrees`
    ];

    setResults({
      height: numericHeight,
      horizontalVelocity: numericHorizontalVelocity,
      verticalVelocity: verticalVelocity.toFixed(2),
      angle: angleDegrees.toFixed(2),
      calculationSteps,
      chartData
    });
  };

  const exampleData = {
    height: '20',
    horizontalVelocity: '15',
    verticalVelocity: '19.81',
    angle: '52.43'
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-purple-100 to-pink-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Angle of Impact Calculator | Projectile Motion Tool</title>
          <meta name="description" content="Calculate the angle of impact for a projectile based on its height and horizontal velocity. This calculator helps determine the angle at which a projectile hits the ground after being launched horizontally." />
          <meta name="keywords" content="angle of impact calculator, projectile motion calculator, physics calculator, impact angle calculator" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-pink-600 mr-4">🎯</div>
            <h1 className="text-3xl font-bold text-gray-800">Angle of Impact Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Calculate the angle of impact for a projectile based on its height and horizontal velocity. This calculator helps determine the angle at which a projectile hits the ground after being launched horizontally.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Height (meters):</label>
              <input
                type="text"
                name="height"
                value={inputData.height}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter height in meters (e.g., 20)"
              />
            </div>
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Horizontal Velocity (m/s):</label>
              <input
                type="text"
                name="horizontalVelocity"
                value={inputData.horizontalVelocity}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter horizontal velocity in m/s (e.g., 15)"
              />
            </div>
          </div>

          <button
            onClick={calculateAngleOfImpact}
            className="w-full py-3 px-4 bg-pink-600 text-white font-medium rounded-lg hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate Angle of Impact
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {results && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Impact Calculation</h3>
                  <p className="text-lg">Height: {results.height} meters</p>
                  <p className="text-lg">Horizontal Velocity: {results.horizontalVelocity} m/s</p>
                  <p className="text-lg">Vertical Velocity at Impact: {results.verticalVelocity} m/s</p>
                  <p className="text-lg font-bold mt-4">Angle of Impact: {results.angle}°</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculation Details</h3>
                  <p className="text-lg">Based on projectile motion physics</p>
                  <p className="text-lg">Assumes no air resistance</p>
                  <p className="text-lg">Projectile launched horizontally</p>
                </div>
              </div>

              {results.chartData && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                  <Bar
                    data={results.chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Velocity (m/s)'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Velocity Components'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Velocity Components at Impact'
                        }
                      }
                    }}
                  />
                </div>
              )}

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculation Steps</h3>
                <div className="space-y-4">
                  {results.calculationSteps.map((step, index) => (
                    <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                      <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-blue-600">📝</span></h2>
          <div className="text-gray-600">
            <p>Here's an example calculation for a projectile launched from a height of 20 meters with a horizontal velocity of 15 m/s:</p>
            
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Height: {exampleData.height} meters</li>
                <li>Horizontal Velocity: {exampleData.horizontalVelocity} m/s</li>
                <li>Vertical Velocity at Impact: {exampleData.verticalVelocity} m/s</li>
                <li>Angle of Impact: {exampleData.angle}°</li>
              </ul>

              <h4 className="mb-4">Calculation Method:</h4>
              <ol className="list-decimal pl-6 mb-4">
                <li>Calculate time of flight using the height</li>
                <li>Calculate vertical velocity using time of flight and gravity</li>
                <li>Calculate angle using arctangent of vertical/horizontal velocity ratio</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use <span className="text-blue-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Angle of Impact Calculator:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Enter Parameters:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input the height from which the projectile is launched</li>
                  <li>Input the horizontal velocity of the projectile</li>
                </ul>
              </li>
              <li><strong>Click Calculate:</strong> The calculator will determine:
                <ul className="list-disc pl-6 mt-2">
                  <li>The vertical velocity at impact</li>
                  <li>The angle of impact</li>
                  <li>The calculation details</li>
                </ul>
              </li>
              <li><strong>Interpret Results:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Review the calculated angle of impact</li>
                  <li>Check the calculation steps</li>
                  <li>View the visual representation of velocity components</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-blue-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>Angle of impact is measured from the horizontal</li>
                <li>Higher velocities result in shallower impact angles</li>
                <li>Greater heights result in steeper impact angles</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Angle of Impact <span className="text-blue-600">🎯</span></h2>
          <div className="text-gray-600">
            <p>
              The angle of impact is the angle at which a projectile hits a surface. In projectile motion, this angle depends on:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Height:</strong> The higher the launch point, the steeper the impact angle</li>
              <li><strong>Horizontal Velocity:</strong> The faster the horizontal speed, the shallower the impact angle</li>
              <li><strong>Gravity:</strong> Accelerates objects downward, affecting the vertical component of velocity</li>
              <li><strong>Projectile Motion:</strong> The parabolic path followed by objects under constant acceleration</li>
            </ul>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Best Practices <span className="text-blue-600">✨</span></h2>
          <div className="text-gray-600">
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Units:</strong> Ensure height is in meters and velocity in m/s for accurate results</li>
              <li><strong>Assumptions:</strong> This calculator assumes no air resistance and horizontal launch</li>
              <li><strong>Context:</strong> Understand the physical scenario your calculation represents</li>
              <li><strong>Validation:</strong> Double-check your inputs and results for reasonableness</li>
            </ul>
            <p className="italic mt-4">
              Note: Real-world scenarios may involve air resistance and other factors not accounted for in this simplified model.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default AngleOfImpactCalculator;